import {TextureType as PlatformTextureType} from "@cm/material-nodes"
import {z} from "zod"
import type {Format, NormalY, InputMapsInfo, MaterialDetails} from "#material-maps-exporter/core/types"
import {formats, normalsY, resolutions} from "#material-maps-exporter/core/types"
import {estimateCommonSize} from "#material-maps-exporter/targets/pbr/image-processing-graph"
import {getImageMapInfos} from "#material-maps-exporter/core/image-processing-graph"
import type {
    Config,
    ConvertedMap,
    Engine,
    InfoFileFormat,
    Request,
    PbrExportDefaultOutputType,
    Settings,
    TextureType,
    Workflow,
} from "#material-maps-exporter/targets/pbr/types"
import {
    DEFAULT_PBR_EXPORT_CONVERSIONS,
    PBR_EXPORT_CONFIG_SCHEMA,
    SRGB_TEXTURE_TYPES_FOR_ENGINE,
    TEXTURE_TYPES_FOR_ENGINE,
    engines,
    workflows,
} from "#material-maps-exporter/targets/pbr/types"
import {normalYToString, resolutionToString, workflowToString} from "#material-maps-exporter/targets/pbr/utils"

function anisotropyDescriptionForEngine(engine: Engine):
    | {
          "Anisotropy Strength": string
          "Anisotropy Rotation": string
      }
    | {
          Anisotropy: string
      } {
    switch (engine) {
        case "cm":
            return {Anisotropy: "As one map"}
        case "corona":
            return {"Anisotropy Strength": "0.5 ... 1.0", "Anisotropy Rotation": "Clockwise"}
        case "vray":
            return {"Anisotropy Strength": "0 ... 1.0", "Anisotropy Rotation": "Clockwise"}
        case "cycles":
            return {"Anisotropy Strength": "0 ... 1.0", "Anisotropy Rotation": "Counter-clockwise"}
        default:
            throw Error(`Unsupported engine: ${engine}`)
    }
}

function infoEntriesForSettings(settings: Settings, materialDetails: MaterialDetails, mapsInfo: InputMapsInfo) {
    const imageMapsInfo = getImageMapInfos(mapsInfo)

    const productName = materialDetails.tagAssignments[0]?.tag.name ?? ""
    const physicalHeight = imageMapsInfo.length > 0 ? imageMapsInfo[0].physicalHeight.toFixed(2) : "?"
    const physicalWidth = imageMapsInfo.length > 0 ? imageMapsInfo[0].physicalWidth.toFixed(2) : "?"
    const displacementScale = imageMapsInfo.find((info) => info.textureType === PlatformTextureType.Displacement)?.displacementScale?.toFixed(2)

    const {widthPx: width, heightPx: height, dpi: estimatedDpi} = estimateCommonSize(imageMapsInfo, settings.resolution)
    const dpi = estimatedDpi?.toFixed(0) ?? "?"
    const resolution = settings.resolution === "dpi72" ? "low" : "original"

    const entries: [string, string][] = []
    entries.push(["Product name", productName])
    entries.push(["Article ID", `${materialDetails.articleId ?? "None"}`])
    entries.push(["Exported for workflow", `${workflowToString(settings.workflow)}`])
    entries.push(["Resolution", `${dpi} DPI (${resolution})`])
    entries.push(["Width", `${physicalWidth} cm (${width} px)`])
    entries.push(["Height", `${physicalHeight} cm (${height} px)`])
    entries.push(["Normal map orientation", `${normalYToString(settings.normalY)}`])
    if (displacementScale !== undefined) entries.push(["Displacement scale", `${displacementScale} cm`])
    Object.entries(anisotropyDescriptionForEngine(settings.engine)).map(([key, value]) => entries.push([key, value]))
    entries.push([
        "Transmission",
        "The amount and color of light transmitted through material surface. Black indicates opaque and white either fully transparent or highly transmissive areas",
    ])

    return entries
}

/** The info file's bytes, as the requested format writes them. */
function renderInfoFile(entries: [string, string][], format: InfoFileFormat): string {
    return format === "json" ? JSON.stringify(Object.fromEntries(entries), null, 2) : entries.map(([key, value]) => `${key}: ${value}`).join("\n")
}

/**
 * The config an export request becomes: the request's five settings, a filename per map, and the info
 * file rendered — everything the job graph needs and everything a later reader may want to know.
 *
 * The filename callbacks answer `null` for "no schema entry", in which case a map falls back to a name
 * built from the material and the info file to `<name>.<ext>`.
 */
export async function exportConfigForExportRequest(
    request: Request,
    materialDetails: MaterialDetails,
    mapsInfo: InputMapsInfo,
    generateConvertedMapFilename: (settings: Settings, mapType: TextureType) => Promise<string | null>,
    generateInfoFilename: (settings: Settings, format: InfoFileFormat) => Promise<string | null>,
): Promise<Config> {
    const {settings, info} = request
    const {workflow, engine, format, normalY, resolution} = settings

    const maps: ConvertedMap[] = await Promise.all(
        TEXTURE_TYPES_FOR_ENGINE[workflow][engine].map(async (mapType) => ({
            mapType,
            filename: (await generateConvertedMapFilename(settings, mapType)) ?? generateDefaultConvertedMapFilename(materialDetails, mapType, format),
        })),
    )

    const infoFilename = (await generateInfoFilename(settings, info.format)) ?? `${info.name}.${info.format === "json" ? "json" : "txt"}`

    return {
        schema: PBR_EXPORT_CONFIG_SCHEMA,
        name: request.name,
        workflow,
        engine,
        format,
        normalY,
        resolution,
        maps,
        info: {name: infoFilename, content: renderInfoFile(infoEntriesForSettings(settings, materialDetails, mapsInfo), info.format)},
    }
}

function generateDefaultConvertedMapFilename(materialDetails: MaterialDetails, mapName: string, ext: Format) {
    return materialDetails.articleId != "" && materialDetails.articleId != null
        ? `${materialDetails.articleId}-${mapName}.${ext}`
        : `${materialDetails.legacyId}-${materialDetails.name}-${mapName}.${ext}`
}

export function useLinearSpace(settings: Settings, mapName: string) {
    return settings.format === "exr" || !SRGB_TEXTURE_TYPES_FOR_ENGINE[settings.workflow][settings.engine].map((x): string => x).includes(mapName)
}

// Factory functions

export function exportSettings(
    workflow: Settings["workflow"],
    engine: Settings["engine"],
    normalY: Settings["normalY"],
    format: Settings["format"],
    resolution: Settings["resolution"],
): Settings {
    return {workflow, engine, normalY, format, resolution}
}

// Export requests

// The settings every default export shares. Format and resolution are not among them: those are what
// the five defaults differ by, and they come from `DEFAULT_PBR_EXPORT_CONVERSIONS`.
export const defaultExportWorkflow: Workflow = "metalnessRoughness"
export const defaultExportEngine: Engine = "vray"
export const defaultExportNormalY: NormalY = "y+up"

/**
 * Every export that exists is one folder holding a single conversion plus its `info` text file. Only
 * the folder name differs: the defaults are named after their format and resolution, a custom export
 * after the material.
 */
export function exportRequestForSettings(settings: Settings, name: string): Request {
    return {name, settings, info: {name: "info", format: "text"}}
}

/** The five default exports as metadata: what each one is, and how it is written for a person. */
export function defaultPbrExportOptions() {
    // `Object.keys` loses the key type; the record's own type is what guarantees these five are
    // exactly the default output types, and its declaration order is the order rendered.
    const defaultOutputTypes = Object.keys(DEFAULT_PBR_EXPORT_CONVERSIONS) as PbrExportDefaultOutputType[]

    return defaultOutputTypes.map((materialOutputType) => {
        const {format, resolution} = DEFAULT_PBR_EXPORT_CONVERSIONS[materialOutputType]
        return {materialOutputType, format, resolution, displayName: `${format.toUpperCase()} - ${resolutionToString(resolution)}`}
    })
}

// Reading stored configs

// Parsed against the vocabularies rather than read off the type: stored rows predate some of the fields.
const settingsSchema = z.object({
    workflow: z.enum(workflows),
    engine: z.enum(engines),
    format: z.enum(formats),
    normalY: z.enum(normalsY),
    resolution: z.enum(resolutions),
})

const storedConfigSchema = settingsSchema.extend({
    schema: z.literal(PBR_EXPORT_CONFIG_SCHEMA),
    name: z.string(),
    maps: z.array(z.object({mapType: z.string(), filename: z.string()})),
    info: z.object({name: z.string(), content: z.string()}),
})

// The encoding rows were written in before `pbrExportConfig/0.0.2`: a folder tree of conversions and
// info files, of which every export ever produced held exactly one of each. Only the fields the flat
// shape keeps are read; `type`, `materialId`, `sourceInfo` and a map's `data` had no reader left.
const legacyRootSchema = z.object({root: z.object({type: z.literal("exportFolder"), name: z.string(), content: z.array(z.unknown())})})
const legacyFolderSchema = z.object({type: z.literal("exportFolder"), content: z.array(z.unknown())})
const legacyConversionSchema = z.object({type: z.literal("conversionConfig"), maps: z.array(z.object({name: z.string(), filename: z.string()})).optional()})
const legacyTextFileSchema = z.object({type: z.literal("exportTextFile"), name: z.string(), content: z.string()})
const legacyJsonFileSchema = z.object({type: z.literal("exportJsonFile"), name: z.string(), content: z.record(z.string())})

function parseLegacyPbrExportConfig(value: unknown): Config | undefined {
    const root = legacyRootSchema.safeParse(value)
    if (!root.success) return undefined

    const conversions: {settings: unknown; maps: ConvertedMap[]}[] = []
    const infoFiles: Config["info"][] = []
    const walk = (items: unknown[]) => {
        for (const item of items) {
            const folder = legacyFolderSchema.safeParse(item)
            if (folder.success) {
                walk(folder.data.content)
                continue
            }
            const conversion = legacyConversionSchema.safeParse(item)
            if (conversion.success) {
                conversions.push({settings: item, maps: (conversion.data.maps ?? []).map(({name, filename}) => ({mapType: name, filename}))})
                continue
            }
            const text = legacyTextFileSchema.safeParse(item)
            if (text.success) {
                infoFiles.push({name: text.data.name, content: text.data.content})
                continue
            }
            const json = legacyJsonFileSchema.safeParse(item)
            if (json.success) infoFiles.push({name: json.data.name, content: JSON.stringify(json.data.content, null, 2)})
        }
    }
    walk(root.data.root.content)

    // Identity is the one conversion's five settings, read exactly: an export holding two conversions
    // has no single identity, and a config missing a field is not a config with that field's default.
    if (conversions.length !== 1 || infoFiles.length === 0) return undefined
    const settings = settingsSchema.safeParse(conversions[0].settings)
    if (!settings.success) return undefined

    return {schema: PBR_EXPORT_CONFIG_SCHEMA, name: root.data.root.name, ...settings.data, maps: conversions[0].maps, info: infoFiles[0]}
}

/**
 * The only door to a stored config: the current shape parsed, the legacy tree lifted to it, anything
 * else `undefined`. Never throws — a config missing a setting is a known legacy population, and the
 * outputs panel reads configs inside a `computed`, where a throw would take the whole material's
 * outputs down with it, delete button included.
 *
 * Discrimination is on the new `schema` value, so rows predating the field still resolve as legacy,
 * while a blob carrying a version this code does not know is refused rather than misread as one.
 */
export function parseStoredPbrExportConfig(value: unknown): Config | undefined {
    const stored = storedConfigSchema.safeParse(value)
    if (stored.success) return stored.data

    const versioned = z.object({schema: z.string().startsWith("pbrExportConfig/")}).safeParse(value)
    if (versioned.success) return undefined

    return parseLegacyPbrExportConfig(value)
}

/**
 * The five settings of a config — the whole of a custom export's identity, and what the outputs UI
 * shows. Picked here rather than spread at the call sites, so `maps`, `info` and the rest never leak
 * into a comparison.
 */
export function pbrExportSettingsForConfig(config: Config): Settings {
    const {workflow, engine, format, normalY, resolution} = config
    return {workflow, engine, format, normalY, resolution}
}

/**
 * Whether two exports are the same export. Every duplicate check compares through this, so that a
 * UI guard cannot decide identity differently from the mutation it is guarding.
 */
export function pbrExportSettingsEqual(a: Settings, b: Settings): boolean {
    return a.workflow === b.workflow && a.engine === b.engine && a.format === b.format && a.normalY === b.normalY && a.resolution === b.resolution
}
